/**
 * Station services feature (order `FEATURE_ORDER.stationServices`; M3 plan R16, §2.9 flows 2 and 10). Reach mode
 * only; in the sandbox it does nothing.
 *
 * - `init` registers M3's four tabs (Charts, Parts, Scanner, Home) into `ctx.reach.services`, where M2's station
 *   screen can find them after the bridge.
 * - `frame` takes `core.uiRequest` exactly once (never while paused) and routes it:
 *   - `services`: opens the services panel for that station (host.ts), which holds a `ui.modal` count and an Esc
 *     handler while open; closing it relocks the pointer and autosaves (`persist('auto')`).
 *   - `sponsor`: "Relay ⟨A⟩ ↔ ⟨B⟩ is online. Who sponsors the relink?" → `chooseSponsor`. Sponsoring is required,
 *     so there is no cancel: Esc and P are held back from the dialog while it is open (pad B still answers with
 *     the last option; see the R16 report).
 *   - `confirmReboot`: "Begin the reboot? Defend the relay for ⟨m:ss⟩." → `beginReboot`; a refusal is shown.
 * - While the panel is open, `frame` feeds it the pad through M1's `stepPadNav` (allocation-free when idle).
 */
import type { FrameInfo, GameContext, GameFeature, UiState } from '../../app/feature'
import { FEATURE_ORDER } from '../../app/feature'
import { logger } from '../../core/logger'
import { defaultFactionsConfig, type FactionsConfig } from '../../galaxy/layout/config'
import type { ReachRuntime } from '../../reach/runtime/types'
import { loadStandInConfig, standInPrices, type StandInPrices } from '../../reach/standin'
import { showModal, type ModalSpec } from '../../reach/ui/modal'
import {
  createServicePanels,
  isFactionKey,
  loadServicesExternals,
  openServicesPanel,
  REBOOT_BEGIN,
  rebootSeconds,
  rebootSpec,
  resultMessage,
  sponsorSpec,
  stationTypeLabel,
  takeUiRequest,
  type ServicesExternals,
  type ServicesHostOptions,
  type ServicesPanelHandle,
  type UiRequestRoutes,
} from '../../reach/ui/services'

export interface StationServicesDeps {
  openPanel: (o: ServicesHostOptions) => ServicesPanelHandle
  showModal: (root: HTMLElement, spec: ModalSpec, ui?: UiState | null) => Promise<string>
  factions: FactionsConfig
  prices: StandInPrices
  externals: ServicesExternals
}

export interface StationServicesFeature extends GameFeature {
  /** The open services panel, or null. */
  readonly panel: ServicesPanelHandle | null
  /** Relink dialogs (sponsor, reboot confirmation) waiting for an answer. */
  readonly pendingDialogs: number
  frame(ctx: GameContext, f: FrameInfo): void
  dispose(): void
}

function defaultDeps(): StationServicesDeps {
  return {
    openPanel: openServicesPanel,
    showModal,
    factions: defaultFactionsConfig(),
    prices: standInPrices(loadStandInConfig()),
    externals: loadServicesExternals(),
  }
}

/**
 * Keeps Esc (the key, and P or pad Start through the loop's Esc stack) from answering a dialog that must be
 * answered. Call just before `showModal` (so the key listener runs before the dialog's); the returned function
 * pushes the stack guard above the dialog's handler, and the one it returns releases both.
 */
function holdEscape(ui: UiState): () => () => void {
  const swallow = (e: KeyboardEvent): void => {
    if (e.code !== 'Escape' || e.defaultPrevented) return
    e.preventDefault()
    e.stopPropagation()
  }
  const consume = (): boolean => true
  window.addEventListener('keydown', swallow, { capture: true })
  return () => {
    ui.escape.push(consume)
    return () => {
      window.removeEventListener('keydown', swallow, { capture: true })
      const i = ui.escape.lastIndexOf(consume)
      if (i >= 0) ui.escape.splice(i, 1)
    }
  }
}

export function createStationServicesFeature(overrides: Partial<StationServicesDeps> = {}): StationServicesFeature {
  let deps: StationServicesDeps | null = null
  let panel: ServicesPanelHandle | null = null
  let routes: UiRequestRoutes | null = null
  let disposed = false
  const sponsorOpen = new Set<string>()
  let rebootOpen = false

  function routesFor(ctx: GameContext, core: ReachRuntime, d: StationServicesDeps): UiRequestRoutes {
    const root = ctx.overlayRoot
    return {
      services(stationId: string): void {
        if (panel !== null) {
          logger.debug('Station services: a panel is already open', { open: panel.station.id, requested: stationId })
          return
        }
        const station = core.session.layout?.stations.find((s) => s.id === stationId)
        if (!station) {
          logger.warn('Station services: no such station in this system', { stationId })
          return
        }
        const faction = d.factions.factions[station.faction]?.name ?? station.faction
        panel = d.openPanel({
          root,
          ui: ctx.ui,
          core,
          station,
          panels: core.services.list(station),
          title: station.name,
          subtitle: `${faction} · ${stationTypeLabel(station.type)}`,
          onClose: (): void => {
            panel = null
            if (!disposed) core.persist('auto')
          },
        })
      },

      sponsor(relayId: string): void {
        if (sponsorOpen.has(relayId)) return
        sponsorOpen.add(relayId)
        const hold = holdEscape(ctx.ui)
        const answer = d.showModal(root, sponsorSpec(core, relayId, d.factions), ctx.ui)
        const release = hold()
        void answer.then((choice) => {
          release()
          sponsorOpen.delete(relayId)
          if (disposed || !isFactionKey(choice, d.factions)) return
          const r = core.command({ kind: 'chooseSponsor', relayId, faction: choice })
          if (!r.ok) logger.warn('Station services: the sponsor was not recorded', { relayId, error: r.error, message: r.message })
        })
      },

      confirmReboot(relayId: string): void {
        if (rebootOpen) return
        rebootOpen = true
        const seconds = rebootSeconds(core, relayId, d.externals.rebootSeconds)
        void d.showModal(root, rebootSpec(core, relayId, seconds), ctx.ui).then((choice) => {
          rebootOpen = false
          if (disposed || choice !== REBOOT_BEGIN) return
          const r = core.command({ kind: 'beginReboot', relayId })
          if (r.ok) return
          void d.showModal(root, { title: 'Reboot not started', body: resultMessage(r).text, buttons: [{ id: 'ok', label: 'OK', primary: true }] }, ctx.ui)
        })
      },
    }
  }

  return {
    name: 'stationServices',
    order: FEATURE_ORDER.stationServices,
    get panel(): ServicesPanelHandle | null {
      return panel
    },
    get pendingDialogs(): number {
      return sponsorOpen.size + (rebootOpen ? 1 : 0)
    },

    init(ctx: GameContext): void {
      const core = ctx.reach
      if (core === null) return
      const d: StationServicesDeps = { ...defaultDeps(), ...overrides }
      deps = d
      for (const p of createServicePanels({ prices: d.prices, externals: d.externals })) core.services.register(p)
      routes = routesFor(ctx, core, d)
    },

    frame(ctx: GameContext, f: FrameInfo): void {
      const core = ctx.reach
      if (core === null || deps === null || routes === null) return
      const p = panel
      if (p !== null) {
        if (f.paused) p.disarmPad()
        else p.stepPad(ctx.input.pad, f.dt)
      }
      if (core.uiRequest !== null && !f.paused) takeUiRequest(core, routes)
    },

    dispose(): void {
      disposed = true
      panel?.close()
      panel = null
      routes = null
      deps = null
    },
  }
}

const feature: StationServicesFeature = createStationServicesFeature()

export default feature
